import { useState, useRef, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, Typography, Box, useMediaQuery,
    useTheme, Skeleton
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Image } from "mui-image";
import { formatEpochToDateTime } from "../../utils";
import { Certificate } from "../../types";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type EventCertificatePreviewInput = {
    open: boolean,
    onClose: (event: any) => void,
    certificates: Certificate[]
}


function CertificatePreview(props: EventCertificatePreviewInput) {
    const { open, onClose, certificates } = props;

    const theme = useTheme();
    const [numPages, setNumPages] = useState<Record<number, number>>({});
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef.current]);

    const onDocumentLoadSuccess = (data: any, index: number) => {
        setNumPages((prev) => ({
            ...prev,
            [index]: data.numPages,
        }));
    };

    const renderCertificate = (certificate: Certificate, index: number) => {
        if (!certificate) return <Typography>No certificate available.</Typography>;

        const presignedUrl = certificate.certificatePresignedURL;

        if (!presignedUrl) {
            return (
                <Box key={index} sx={{ marginBottom: 4, width: "100%", textAlign: "center" }}>
                    <Skeleton variant="rectangular" width="100%" height={200} />
                </Box>
            );
        }

        return (
            <Box key={index} sx={{ marginBottom: 4, width: "100%", textAlign: "center" }}>
                <Typography variant="h6" align="center">
                    {certificate.certificateName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Document Name: {certificate.ceritificateId}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Uploaded On: {formatEpochToDateTime(certificate.createdEpoch)}
                </Typography>

                {certificate.fileType === "image" && (
                    <Image
                        src={presignedUrl}
                        alt="Certificate Preview"
                    />
                )}

                {certificate.fileType === "pdf" && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', overflowY: 'auto', width: '100%' }}>
                        <Document
                            file={presignedUrl}
                            onLoadSuccess={(data) => onDocumentLoadSuccess(data, index)}
                            loading={<Skeleton variant="rectangular" width="100%" height={100} />}
                        >
                            {Array.from({ length: numPages[index] || 0 }, (_, pageIndex) => (
                                <Page
                                    key={`page_${pageIndex + 1}`}
                                    pageNumber={pageIndex + 1}
                                    width={containerWidth}
                                />
                            ))}
                        </Document>
                    </Box>
                )}

                {!["image", "pdf"].includes(certificate.fileType) && (
                    <Typography>Unsupported certificate format.</Typography>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Certifications</Typography>
                    <CancelIcon fontSize="medium" onClick={onClose} style={{ cursor: "pointer" }} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ height: { xs: 'calc(var(--app-vh, 1vh) * 60)', md: 'calc(var(--app-vh, 1vh) * 70)' }, overflowY: 'auto', padding: 2 }}>
                <Box ref={containerRef}>
                    {certificates.map((certificate, index) => renderCertificate(certificate, index))}
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default CertificatePreview;
